import Image from 'next/image';
import { Box } from '@mui/material';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ReduxState } from 'ducks';
import styles from './BusRouteSelect.module.css';
import { useMapDisplayContext } from '../MapDisplayContext';

export const BusRouteSelect: React.FC = () => {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const digitalMap = useSelector((state: ReduxState) => state.universal.digitalMap.map);
  const { filters, setFilters } = useMapDisplayContext();
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Effect to handle outside click for dropdown
  React.useEffect(() => {
    const handleOutsideClick = ({ target }: Event) => {
      if (target instanceof Node && !dropdownRef?.current?.contains(target)) {
        setDropdownOpen(false);
      }
    };

    window.document.addEventListener('mousedown', handleOutsideClick, {
      capture: true,
    });
    window.document.addEventListener('touchstart', handleOutsideClick, {
      capture: true,
    });
    return () => {
      window.document.removeEventListener('mousedown', handleOutsideClick, {
        capture: true,
      });
      window.document.removeEventListener('touchstart', handleOutsideClick, {
        capture: true,
      });
    };
  }, [dropdownOpen]);

  const selectedRoute = filters.busRouteId
    ? digitalMap?.bus_routes.find((route) => route.id === filters.busRouteId)
    : null;

  const selectedRouteLabel = selectedRoute ? selectedRoute.name : t('All Routes');

  const handleRouteSelect = (routeId: string | null) => {
    setFilters({
      ...filters,
      busRouteId: routeId,
    });
  };

  return (
    <nav className={styles.navigationMenu} ref={dropdownRef}>
      {!dropdownOpen && (
        <div className={styles.menuItem} onClick={() => setDropdownOpen(true)}>
          {!selectedRoute && (
            <Image
              priority
              width={15}
              height={15}
              src="/static/images/map/multicolor.svg"
              alt={selectedRouteLabel}
            />
          )}
          {selectedRoute && (
            <div
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: selectedRoute.color,
                marginRight: '2px',
              }}
            />
          )}
          <h2 className={styles.label}>{selectedRouteLabel}</h2>
          <Image
            priority
            width={15}
            height={15}
            src="/static/images/map/down_chevron.svg"
            alt="down arrow"
          />
        </div>
      )}
      {dropdownOpen && (
        <>
          <div
            className={styles.menuItem}
            onClick={() => {
              setDropdownOpen(false);
              handleRouteSelect(null);
            }}
          >
            <Image
              priority
              width={15}
              height={15}
              src="/static/images/map/multicolor.svg"
              alt={t('All Routes')}
            />
            <h2 className={styles.label}>{t('All Routes')}</h2>
            <Box ml="auto">
              <Image
                priority
                width={15}
                height={15}
                src="/static/images/map/down_chevron.svg"
                alt="down arrow"
              />
            </Box>
          </div>
          {digitalMap?.bus_routes.map((route) => (
            <div
              key={route.id}
              className={styles.menuItem}
              onClick={() => {
                setDropdownOpen(false);
                handleRouteSelect(route.id);
              }}
            >
              <div
                style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  backgroundColor: route.color,
                  marginRight: '2px',
                }}
              />
              <h2 className={styles.label}>{route.name}</h2>
            </div>
          ))}
        </>
      )}
    </nav>
  );
};
